import {
  ANALYTICS_QUERY_MAX_RELATIVE_FILTERS,
  AnalyticsValueType,
  CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION,
} from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-constants";
import {
  AnalyticsColumnOutputRefSchema,
  AnalyticsRoutedRefSchema,
  analyticsOutputSelectionKey,
  type AnalyticsExpressionRef,
} from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-refs";
import {
  AnalyticsConditionExpressionSchema,
  AnalyticsFilterValueSchema,
  AnalyticsRelativeFilterSchema,
} from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-schema";
import type {
  AnalyticsComparisonCondition,
  AnalyticsConditionExpression,
  CustomAnalyticsSemanticQuery,
} from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-types";
import {
  CustomAnalyticsQueryComparisonOperatorSchema,
  CustomAnalyticsQueryScalarValueSchema,
} from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/shared/custom-analytics-query-primitives";
import * as z from "zod";

export const customAnalyticsReportFilterVersion =
  CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION;

export const customAnalyticsFilterStages = [
  "filterRecords",
  "filterRelative",
  "filterGroups",
] as const;
export const customAnalyticsFilterStageLimits = {
  filterRecords: 20,
  filterRelative: ANALYTICS_QUERY_MAX_RELATIVE_FILTERS,
  filterGroups: 20,
};
export type CustomAnalyticsFilterStage =
  (typeof customAnalyticsFilterStages)[number];
export interface CustomAnalyticsReportFilterLocation {
  stage: CustomAnalyticsFilterStage;
  index: number;
}
export type CustomAnalyticsFilterCondition =
  AnalyticsConditionExpression<AnalyticsExpressionRef>;

// Bound the recursive payload before entering the recursive AST schemas.
const boundedFilterPayload = z.unknown().superRefine((value, ctx) => {
  const pending = [{ value, depth: 0 }];
  let count = 0;
  while (pending.length) {
    const entry = pending.pop();
    if (!entry) {
      break;
    }
    if (++count > 10_000 || entry.depth > 32) {
      ctx.addIssue({
        code: "custom",
        message: "The filter expression is too complex.",
      });
      return;
    }
    if (typeof entry.value === "string" && entry.value.length > 10_000) {
      ctx.addIssue({
        code: "custom",
        message: "The filter value is too long.",
      });
      return;
    }
    if (entry.value && typeof entry.value === "object") {
      for (const child of Object.values(entry.value)) {
        pending.push({ value: child, depth: entry.depth + 1 });
      }
    }
  }
});

export const CustomAnalyticsReportFilterStateSchema = boundedFilterPayload.pipe(
  z.strictObject({
    filterRecords: z
      .array(AnalyticsConditionExpressionSchema)
      .max(20)
      .default([]),
    filterRelative: z
      .array(AnalyticsRelativeFilterSchema)
      .max(ANALYTICS_QUERY_MAX_RELATIVE_FILTERS)
      .default([]),
    filterGroups: z
      .array(AnalyticsConditionExpressionSchema)
      .max(20)
      .default([]),
  }),
);
export type CustomAnalyticsReportFilterState = z.infer<
  typeof CustomAnalyticsReportFilterStateSchema
>;

export const CustomAnalyticsReportFilterDraftSchema = z.strictObject({
  version: z.literal(CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION),
  expectedUpdatedAt: z.date(),
  filters: CustomAnalyticsReportFilterStateSchema,
});
export type CustomAnalyticsReportFilterDraft = z.infer<
  typeof CustomAnalyticsReportFilterDraftSchema
>;

export const CustomAnalyticsReportFilterFieldSchema = z.strictObject({
  sourceName: z.string(),
  label: z.string(),
  category: z.string().optional(),
  description: z.string().optional(),
  searchTerms: z.array(z.string()).optional(),
  grainLabel: z.string().optional(),
  on: z.union([AnalyticsRoutedRefSchema, AnalyticsColumnOutputRefSchema]),
  valueType: z.enum(AnalyticsValueType),
  arrayElementType: z.enum(AnalyticsValueType).optional(),
  allowedValues: z.array(CustomAnalyticsQueryScalarValueSchema).optional(),
  stages: z.array(z.enum(customAnalyticsFilterStages)),
  defaultStage: z.enum(customAnalyticsFilterStages).nullable(),
  operators: z.array(CustomAnalyticsQueryComparisonOperatorSchema),
  percent: z.boolean(),
  integer: z.boolean(),
});
export type CustomAnalyticsReportFilterField = z.infer<
  typeof CustomAnalyticsReportFilterFieldSchema
>;

export const CustomAnalyticsReportFilterDefinitionSchema = z.strictObject({
  version: z.literal(CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION),
  filters: CustomAnalyticsReportFilterStateSchema,
  fields: z.array(CustomAnalyticsReportFilterFieldSchema),
});
export type CustomAnalyticsReportFilterDefinition = z.infer<
  typeof CustomAnalyticsReportFilterDefinitionSchema
>;

export class InvalidCustomAnalyticsReportFilterError extends Error {}

export function getCustomAnalyticsReportFilterState(
  query: CustomAnalyticsSemanticQuery,
): CustomAnalyticsReportFilterState {
  return CustomAnalyticsReportFilterStateSchema.parse({
    filterRecords: query.filterRecords,
    filterRelative: query.filterRelative,
    filterGroups: query.filterGroups,
  });
}

export function customAnalyticsReportFilterStatesEqual(
  left: CustomAnalyticsReportFilterState | null,
  right: CustomAnalyticsReportFilterState | null,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function getCustomAnalyticsReportEditableFilterField({
  condition,
  fields,
  stage,
}: {
  condition: CustomAnalyticsFilterCondition;
  fields: readonly CustomAnalyticsReportFilterField[];
  stage: CustomAnalyticsFilterStage;
}): CustomAnalyticsReportFilterField | undefined {
  if (condition.type !== "comparison") {
    return undefined;
  }
  return fields.find(
    (field) =>
      field.stages.includes(stage) &&
      analyticsOutputSelectionKey(field.on) ===
        analyticsOutputSelectionKey(condition.on) &&
      isCustomAnalyticsReportFilterValueValid(field, condition),
  );
}

export function isCustomAnalyticsReportFilterValueValid(
  field: CustomAnalyticsReportFilterField,
  condition: AnalyticsComparisonCondition<AnalyticsExpressionRef>,
): boolean {
  if (!field.operators.includes(condition.operation)) {
    return false;
  }
  if (
    condition.operation === "is_null" ||
    condition.operation === "is_not_null"
  ) {
    return true;
  }
  const value = condition.value;
  const isList =
    condition.operation === "in" || condition.operation === "not_in";
  if (isList !== Array.isArray(value)) {
    return false;
  }
  if (
    isList &&
    Array.isArray(value) &&
    value.some((item) => typeof item === "string" && /[\r\n]/.test(item))
  ) {
    return false;
  }
  const values: readonly unknown[] = Array.isArray(value) ? value : [value];
  if (!values.length) {
    return false;
  }
  const valueType =
    field.valueType === "array" ? field.arrayElementType : field.valueType;
  return values.every((item) => {
    if (
      field.allowedValues &&
      item !== null &&
      ((typeof item !== "string" &&
        typeof item !== "number" &&
        typeof item !== "boolean") ||
        !field.allowedValues.includes(item))
    ) {
      return false;
    }
    if (item === null) {
      return condition.operation === "is_distinct_from";
    }
    switch (valueType) {
      case "number":
      case "money":
        return (
          typeof item === "number" &&
          Number.isFinite(item) &&
          (!field.integer || Number.isInteger(item))
        );
      case "boolean":
        return typeof item === "boolean";
      case "date":
      case "datetime":
        if (typeof item === "object") {
          return !isList && AnalyticsFilterValueSchema.safeParse(item).success;
        }
        return (
          typeof item === "string" &&
          (valueType === "date"
            ? z.iso.date().safeParse(item).success
            : z.iso.datetime({ offset: true, local: true }).safeParse(item)
                .success)
        );
      case "string":
      case "id":
        return typeof item === "string";
      case "array":
      case undefined:
        return false;
    }
  });
}

export function editCustomAnalyticsReportFilter({
  filters,
  fields,
  stage,
  index,
  condition,
}: {
  filters: CustomAnalyticsReportFilterState;
  fields: readonly CustomAnalyticsReportFilterField[];
  stage: CustomAnalyticsFilterStage;
  index?: number;
  condition?: CustomAnalyticsFilterCondition;
}): CustomAnalyticsReportFilterState {
  return replaceCustomAnalyticsReportFilters({
    filters,
    fields,
    stage,
    indices: index === undefined ? [] : [index],
    conditions: condition ? [condition] : [],
  });
}

export function replaceCustomAnalyticsReportFilters({
  filters,
  fields,
  stage,
  indices,
  conditions,
}: {
  filters: CustomAnalyticsReportFilterState;
  fields: readonly CustomAnalyticsReportFilterField[];
  stage: CustomAnalyticsFilterStage;
  indices: readonly number[];
  conditions: readonly CustomAnalyticsFilterCondition[];
}): CustomAnalyticsReportFilterState {
  const replaced = new Set(indices);
  if (
    replaced.size !== indices.length ||
    indices.some((index) => {
      const previous = filters[stage][index];
      return (
        !Number.isInteger(index) ||
        !previous ||
        !getCustomAnalyticsReportEditableFilterField({
          condition: previous,
          fields,
          stage,
        })
      );
    })
  ) {
    throw new InvalidCustomAnalyticsReportFilterError(
      "This condition cannot be edited here.",
    );
  }
  if (
    conditions.some(
      (condition) =>
        !getCustomAnalyticsReportEditableFilterField({
          condition,
          fields,
          stage,
        }),
    )
  ) {
    throw new InvalidCustomAnalyticsReportFilterError(
      "Choose a supported operator and value for this column.",
    );
  }
  const insertionIndex = indices.length
    ? Math.min(...indices)
    : filters[stage].length;
  const next: CustomAnalyticsFilterCondition[] = [];
  for (let index = 0; index <= filters[stage].length; index++) {
    if (index === insertionIndex) {
      next.push(...conditions);
    }
    const condition = filters[stage][index];
    if (condition && !replaced.has(index)) {
      next.push(condition);
    }
  }
  return CustomAnalyticsReportFilterStateSchema.parse({
    ...filters,
    [stage]: next,
  });
}

export function clearEditableCustomAnalyticsReportFilters(
  filters: CustomAnalyticsReportFilterState,
  fields: readonly CustomAnalyticsReportFilterField[],
): CustomAnalyticsReportFilterState {
  return CustomAnalyticsReportFilterStateSchema.parse(
    Object.fromEntries(
      customAnalyticsFilterStages.map((stage) => [
        stage,
        filters[stage].filter(
          (condition) =>
            !getCustomAnalyticsReportEditableFilterField({
              condition,
              fields,
              stage,
            }),
        ),
      ]),
    ),
  );
}

export function customAnalyticsReportFilterUsesField(
  condition: CustomAnalyticsFilterCondition,
  field: CustomAnalyticsReportFilterField,
): boolean {
  if (condition.type === "comparison") {
    return (
      analyticsOutputSelectionKey(condition.on) ===
      analyticsOutputSelectionKey(field.on)
    );
  }
  if (condition.type === "not") {
    return customAnalyticsReportFilterUsesField(condition.condition, field);
  }
  return condition.conditions.some((child) =>
    customAnalyticsReportFilterUsesField(child, field),
  );
}
